import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${window.location.origin}/api`;
const CurrencyContext = createContext();

const CURRENCIES = {
  PHP: { code: 'PHP', symbol: '₱', label: 'PH₱', decimals: 0 },
  USD: { code: 'USD', symbol: '$', label: 'US$', decimals: 2 },
};

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => {
    try { return localStorage.getItem('jsf_currency') || 'PHP'; } catch { return 'PHP'; }
  });
  // PHP-base rates: { PHP: 1.0, USD: 0.0177 }
  const [rates, setRates] = useState({ PHP: 1.0, USD: 0.0177 });

  useEffect(() => {
    axios.get(`${API}/currency/rates`)
      .then(res => {
        if (res.data?.rates) setRates(res.data.rates);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem('jsf_currency', currency); } catch (_) { /* noop */ }
  }, [currency]);

  /**
   * Convert a PHP-denominated price to the selected display currency and format it.
   * `amount` is always assumed to be in PHP (the storage currency).
   */
  const formatPrice = useCallback((amount, opts = {}) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '';
    const cfg = CURRENCIES[currency] || CURRENCIES.PHP;
    const rate = rates[currency] || (currency === 'PHP' ? 1.0 : 0.0177);
    const converted = amount * rate;
    const decimals = opts.decimals !== undefined ? opts.decimals : cfg.decimals;
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(converted);
    return opts.noSymbol ? formatted : `${cfg.symbol}${formatted}`;
  }, [currency, rates]);

  const value = {
    currency,
    setCurrency,
    rates,
    currencies: CURRENCIES,
    formatPrice,
    symbol: (CURRENCIES[currency] || CURRENCIES.PHP).symbol,
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  // Defensive default if used outside provider — never throws
  if (!ctx) {
    return {
      currency: 'PHP',
      setCurrency: () => {},
      rates: { PHP: 1.0, USD: 0.0177 },
      currencies: CURRENCIES,
      formatPrice: (amt) => `₱${(amt || 0).toLocaleString()}`,
      symbol: '₱',
    };
  }
  return ctx;
};
